'use client';

import React, { useState, useEffect } from 'react';

import {
  ChevronUp20Filled,
  Document16Filled,
  DocumentPdf20Filled,
} from '@fluentui/react-icons';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  useSpring,
  animated,
  useTransition,
  useChain,
  useSpringRef,
} from 'react-spring';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';
import { useDebouncedCallback } from 'use-debounce';

import { cn } from '@avodah-inc/ui/utils';

import { DEFAULT_ACCORDIAN_ITEM } from '../../atoms/accordion';
import { Button } from '../../atoms/button';
import { Typography } from '../../atoms/typography';
import { Card } from '../card';
import { ColorChip } from './color-chip';
import { SymptomCard } from './symptom-card';

const accordionCardVariants = tv({
  base: 'inline-flex w-full justify-start items-center relative rounded-lg overflow-clip',
  variants: {
    variant: {
      default: 'text-gray-500',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type AdditionalSharedProps = {
  label: string;
  rightSideText?: string | number;
  chips: {
    label: string;
    count: number;
    className?: string;
  }[];
  items?: {
    value: string;
    icd10Code?: string;
    icd10Description?: string;
    chip: {
      label: string;
      count: number;
      className?: string;
    };
    hitContexts?: {
      symptom: string;
      synonyms: string[];
      sentence: string;
      startWord: number;
      color: string;
      workflowStep: string;
    }[];
  }[];
  mainSessionRef?: React.RefObject<HTMLDivElement>;
  showViewPDFButton?: boolean;
  onViewPDF?: () => void;
};

export type AccordionCardTriggerProps =
  AccordionPrimitive.AccordionTriggerProps &
    VariantProps<typeof accordionCardVariants> &
    AdditionalSharedProps;

export const AccordionCardTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionCardTriggerProps
>(
  (
    {
      label,
      rightSideText,
      chips,
      showViewPDFButton,
      onViewPDF,
      className,
      variant,
      ...triggerProps
    },
    ref,
  ) => {
    return (
      <AccordionPrimitive.Trigger
        className={cn(
          'z-50 flex w-full items-center justify-start gap-2 outline-none',
          accordionCardVariants({
            variant,
            className,
          }),
        )}
        ref={ref}
        {...triggerProps}
      >
        <Card className="z-50 flex h-fit w-full flex-col gap-y-3 overflow-clip rounded-lg border-gray-300 p-4 shadow-md">
          <header className="flex flex-row items-center gap-3">
            <Document16Filled className="text-primary" />
            <div className="flex w-full flex-row items-center justify-between gap-2">
              <Typography variant="h5">{label}</Typography>
              <Typography variant="h5">{rightSideText}</Typography>
            </div>
          </header>
          <section className="flex flex-row flex-wrap gap-2">
            {chips.map((chip, index) => (
              <ColorChip
                key={index}
                className={chip.className}
                text={chip.label}
                rightSideContent={chip.count}
                variant="sm"
              />
            ))}
          </section>
          {showViewPDFButton && (
            <section className="flex flex-row items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onPress={onViewPDF}>
                <DocumentPdf20Filled />
                View
              </Button>
            </section>
          )}
        </Card>
      </AccordionPrimitive.Trigger>
    );
  },
);

AccordionCardTrigger.displayName = 'AccordionTrigger';

export type AccordionCardProps = AccordionPrimitive.AccordionImplSingleProps &
  VariantProps<typeof accordionCardVariants> &
  AdditionalSharedProps;

type Card = {
  delay: number;
};

enum TransitionPhase {
  ENTER = 'enter',
  LEAVE = 'leave',
}

export const AccordionCard = React.forwardRef<
  HTMLDivElement,
  AccordionCardProps
>(
  (
    { className = '', variant, showViewPDFButton, onViewPDF, ...props },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [showButton, setShowButton] = useState(false);

    const mountButton = useDebouncedCallback(() => {
      setShowButton(true);
    }, 120);

    const debouncedClose = useDebouncedCallback(() => {
      setShowButton(false);
      setIsMounted(false);
    }, 400);

    const debouncedOpen = useDebouncedCallback(() => {
      setIsMounted(true);
    }, 100);

    const transRef = useSpringRef();
    const animatedTransition = useTransition(isOpen ? props.items : [], {
      ref: transRef,
      initial: { opacity: 0, x: 0, y: -100 },
      from: {
        opacity: 0,
        x: 0,
        y: 0,
      },
      enter: { opacity: 1, x: 0, y: 0 },
      leave: { opacity: 1, x: 0, y: -200 },
      trail: 80,
      reset: Boolean(isOpen),
      config: (item, index, phase) => {
        const enumPhase = phase as TransitionPhase;
        return {
          tension: 150,
          friction: 100,
          duration: enumPhase === TransitionPhase.LEAVE ? 150 : 200,
          delay: enumPhase === TransitionPhase.ENTER ? 0 : index * 0,
        };
      },
    });

    useEffect(() => {
      void transRef.start();
    }, [isMounted]);

    const springRef = useSpringRef();
    const springs = useSpring({
      ref: springRef,
      from: { height: isOpen ? '0px' : '0px' },
      to: { height: isOpen ? '40px' : '0px' },
    });

    const handleClick = () => {
      debouncedClose();
      setIsOpen((prev) => !prev);
    };

    useChain([springRef, transRef], [0, 0.2], 500);

    return (
      <>
        <animated.div
          className={
            'relative flex h-[56px] w-full justify-start backdrop-blur-lg backdrop-filter'
          }
          style={{
            height: springs.height,
          }}
        >
          <div>
            {showButton && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onPress={handleClick}
              >
                <Typography
                  variant="body2"
                  className="text-primary flex items-center gap-x-2"
                >
                  <ChevronUp20Filled /> Show Less
                </Typography>
              </Button>
            )}
          </div>
        </animated.div>
        <AccordionPrimitive.Root
          className={cn(
            accordionCardVariants({
              variant,
              className,
            }),
          )}
          collapsible
          ref={ref}
          type="single"
          {...props}
        >
          <AccordionPrimitive.AccordionItem
            className={cn(
              'relative z-50 flex h-fit w-full flex-col items-center justify-center space-y-4 rounded-lg',
              {
                'mb-1': !isOpen && props.items && props.items.length === 1,
                'mb-4': !isOpen && props.items && props.items.length > 1,
              },
            )}
            value={DEFAULT_ACCORDIAN_ITEM}
          >
            <AccordionCardTrigger
              rightSideText={props.rightSideText}
              chips={props.chips}
              label={props.label}
              showViewPDFButton={showViewPDFButton}
              onViewPDF={onViewPDF}
              onClick={() => {
                mountButton();
                if (!isMounted && !isOpen) {
                  debouncedOpen();
                } else {
                  debouncedClose();
                }
                setIsOpen((prev) => !prev);
              }}
            />
            {!isMounted &&
              props.items?.slice(0, 2).map((item, index) => (
                <div
                  key={item.value}
                  data-state={isMounted}
                  className={cn(
                    `flex h-10 w-full flex-row items-center gap-2 rounded-[14px] bg-gray-${
                      200 - index * 100
                    } absolute`,
                    {
                      'top-[calc(100%-50px)] z-[49] w-[calc(100%-6px)]':
                        index === 0,
                      'top-[calc(100%-44px)] z-[48] w-[calc(100%-10px)]':
                        index === 1,
                      'top-[calc(100%-44px)] z-[47] w-[calc(100%-14px)]':
                        index === 2,
                    },
                  )}
                />
              ))}
            {isMounted &&
              animatedTransition((style, item) => {
                return (
                  <animated.div
                    className={'w-full'}
                    style={{
                      ...style,
                    }}
                  >
                    {item && (
                      <SymptomCard
                        key={item.value}
                        chip={item.chip}
                        icd10Code={item.icd10Code}
                        icd10Description={item.icd10Description}
                        hitContexts={item.hitContexts}
                        mainSessionRef={props.mainSessionRef}
                        className="shadow-none"
                      />
                    )}
                  </animated.div>
                );
              })}
          </AccordionPrimitive.AccordionItem>
        </AccordionPrimitive.Root>
      </>
    );
  },
);

AccordionCard.displayName = 'AccordionCard';
